import { memo } from 'react';

import { Dialog, DialogClose } from '../Dialog';
import { DialogContent, DialogFooter, DialogTitle } from '../DialogLayout';
import { Button, ButtonProps } from '../Button';
import { cn } from '../../lib/styles';

export interface ConfirmationDialogProps {
  title: React.ReactNode;
  content?: React.ReactNode;

  open?: boolean;
  trigger?: React.ReactNode;

  onConfirm?: () => void;
  onCancel?: () => void;

  cancelButtonProps?: ButtonProps & { text?: string };
  confirmButtonProps?: ButtonProps & { text?: string };
  additionalAction?: React.ReactNode;

  className?: string;
  overlayClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  dialogClassName?: string;
  minWidth?: string;
  closeOnInteractOutside?: boolean;
}

const ConfirmationDialogBase = ({
  title,
  content,

  open,
  trigger,

  onConfirm,
  onCancel,

  cancelButtonProps = {},
  confirmButtonProps = {},
  additionalAction,

  className,
  overlayClassName,
  titleClassName,
  contentClassName,
  footerClassName,
  dialogClassName,
  minWidth = 'min-w-[420px]',
  closeOnInteractOutside = true,
}: ConfirmationDialogProps) => {
  const {
    text: cancelText = 'Cancel',
    variant: cancelVariant = 'secondary',
    size: cancelSize = 'lg',
    color: cancelColor = 'default',
    className: cancelClassName,
    ...cancelButtonRest
  } = cancelButtonProps;

  const {
    text: confirmText = 'Confirm',
    variant: confirmVariant = 'secondary',
    size: confirmSize = 'lg',
    color: confirmColor = 'destructive',
    className: confirmClassName,
    ...confirmButtonRest
  } = confirmButtonProps;

  const handleCancel = () => {
    onCancel?.();
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCancel?.();
    }
  };

  return (
    <Dialog
      dialogClassName={cn('z-405', dialogClassName)}
      overlayClassName={overlayClassName}
      className={className}
      trigger={trigger}
      open={open}
      closeOnInteractOutside={closeOnInteractOutside}
      onOpenChange={handleOpenChange}
    >
      <DialogTitle className={cn('border-none pb-2 [&_h2]:normal-case!', minWidth, titleClassName)}>
        {title}
      </DialogTitle>

      {content ? (
        <DialogContent className={cn('pt-0 font-body-sm text-text-tertiary font-medium', contentClassName)}>
          {content}
        </DialogContent>
      ) : null}

      <DialogFooter className={cn('border-none', footerClassName)}>
        <DialogClose disabled={cancelButtonRest.disabled || cancelButtonRest.loading}>
          <Button
            variant={cancelVariant}
            size={cancelSize}
            color={cancelColor}
            className={cancelClassName}
            onClick={handleCancel}
            {...cancelButtonRest}
          >
            {cancelText}
          </Button>
        </DialogClose>

        <Button
          variant={confirmVariant}
          size={confirmSize}
          color={confirmColor}
          className={confirmClassName}
          onClick={handleConfirm}
          {...confirmButtonRest}
        >
          {confirmText}
        </Button>

        {additionalAction}
      </DialogFooter>
    </Dialog>
  );
};

export const ConfirmationDialog = memo(ConfirmationDialogBase);
